<template>
  <div class="covered-tasks mt-4">
    <div class="covered-tasks-header">
      <span class="section-label">Covered Tasks</span>
    </div>
    <div v-if="!groups.length" class="empty-state">--</div>
    <div
      v-for="(group, index) in groups"
      :key="group.address_id"
      :class="['covered-tasks-group', { 'mt-4': index > 0 }]"
    >
      <div class="address-header">
        <i class="fas fa-map-pin mr-2"></i>
        <span>{{ group.address_street }}</span>
      </div>
      <div class="table-panel">
        <table class="covered-tasks-table">
          <thead>
            <tr>
              <th>Task</th>
              <th>Status</th>
              <th>Available until</th>
              <th>Completed On</th>
              <th>Invoice</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="task in group.tasks"
              :key="`${group.address_id}-${task.task_name}-${task.slot_number}`"
            >
              <td>{{ task.task_name }}</td>
              <td>
                <Badge
                  :text="task.status"
                  :color="statusColor(task.status)"
                  variant="chip"
                />
              </td>
              <td>
                <template v-if="task.available_until">
                  <div class="available-until-cell">
                    <span :class="{ 'font-semibold': showDaysChip(task) }">{{
                      shortDate(task.available_until)
                    }}</span>
                    <Badge
                      v-if="showDaysChip(task)"
                      :text="`${task.days_remaining}d`"
                      :color="daysLeftColor(task.days_remaining!)"
                      variant="chip"
                    />
                  </div>
                </template>
                <template v-else>--</template>
              </td>
              <td>
                <template v-if="task.completed_at">{{
                  shortDate(task.completed_at)
                }}</template>
                <template v-else>--</template>
              </td>
              <td>
                <a
                  v-if="task.invoice_id && task.invoice_url"
                  :href="task.invoice_url"
                  class="invoice-link"
                  @click.stop
                  >#{{ task.invoice_id }}</a
                >
                <template v-else>--</template>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import Badge from "@/components/Badge.vue";
import { shortDate } from "@/mixins";
import { CoveredTaskGroup, CoveredTaskItem } from "@/typings/memberships";

withDefaults(
  defineProps<{
    groups: CoveredTaskGroup[];
  }>(),
  { groups: () => [] }
);

const DAYS_NEARING = 60;
const DAYS_WARNING = 30;
const DAYS_DANGER = 14;

function statusColor(status: CoveredTaskItem["status"]) {
  return status === "Completed" ? "green" : "yellow";
}

function showDaysChip(task: CoveredTaskItem): boolean {
  return (
    task.status !== "Completed" &&
    task.days_remaining !== null &&
    task.days_remaining < DAYS_NEARING
  );
}

function daysLeftColor(days: number): "red" | "yellow" | "blue" {
  if (days < DAYS_DANGER) return "red";
  if (days < DAYS_WARNING) return "yellow";
  return "blue";
}
</script>

<style lang="scss" scoped>
.covered-tasks-header {
  margin-bottom: 0.75rem;
}
.section-label {
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--gray);
}
.address-header {
  font-weight: 600;
  font-size: 0.95rem;
  color: #111827;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
}
.table-panel {
  border: 1px solid var(--gray-border-color);
  border-radius: 6px;
  overflow: hidden;
  background: white;
}
.covered-tasks-table {
  width: 100%;
  border-collapse: collapse;
  th,
  td {
    padding: 0.625rem 1rem;
    text-align: left;
  }
  th {
    font-size: 0.7rem;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--gray);
    background: #f9fafb;
    border-bottom: 1px solid var(--gray-border-color);
  }
  tbody tr {
    border-bottom: 1px solid var(--gray-border-color);
    &:last-of-type {
      border-bottom: none;
    }
  }
}
.empty-state {
  color: var(--gray);
}
.available-until-cell {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.font-semibold {
  font-weight: 600;
}
</style>
